import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import useFirstRunTour from '../hooks/useFirstRunTour';

/*
 * Lightweight, dependency-free guided tour (Foundation PRD v0.3 — owner
 * onboarding / first-run experience). No react-joyride / shepherd / intro.js —
 * built with React + lucide-react only, matching the app's inline-style +
 * design-token approach.
 *
 * <GuidedTour> is the generic, presentational engine: a dimmed backdrop, a
 * "spotlight" cut-out around the current target, and a positioned coachmark
 * tooltip with a step counter + Next / Back / Skip (Done on the last step).
 * <OwnerGuidedTour> below wires it to the owner-portal steps + the first-run
 * controller (useFirstRunTour).
 *
 * Robustness / a11y:
 *   - Targets are resolved live by data-tour selector with a short retry window
 *     (covers lazy/late-rendering nav). If a target never appears, the step
 *     degrades to a centred tooltip rather than crashing or pointing at nothing.
 *   - On mobile (<768px) the owner nav is an off-canvas drawer, so anchored
 *     steps fall back to a centred tooltip there too.
 *   - role="dialog"/aria-modal, Esc to close, ArrowLeft/Right + Enter to step,
 *     focus moves into the tooltip and is trapped, and is restored on close.
 */

const PAD = 8;     // spotlight padding around the target rect
const GAP = 14;    // gap between target and tooltip
const TIP_W = 320; // tooltip width (px)

// Decide where to float the tooltip relative to the target. Left-rail nav items
// get the tooltip to their right; otherwise prefer below, then above, then
// centre. Returns null to mean "centre on screen".
function computePos(rect, tipW, tipH, vw, vh) {
  const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
  // Left sidebar target → place to the right of it.
  if (rect.right < vw * 0.4) {
    const left = rect.right + GAP;
    if (left + tipW + 16 <= vw) {
      return { top: clamp(rect.top, 16, vh - tipH - 16), left };
    }
  }
  // Below the target.
  if (rect.bottom + GAP + tipH + 16 <= vh) {
    return { top: rect.bottom + GAP, left: clamp(rect.left, 16, vw - tipW - 16) };
  }
  // Above the target.
  if (rect.top - GAP - tipH - 16 >= 0) {
    return { top: rect.top - GAP - tipH, left: clamp(rect.left, 16, vw - tipW - 16) };
  }
  return null; // centre
}

export default function GuidedTour({ open, steps, onClose }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);   // viewport rect of target, or null
  const [pos, setPos] = useState(null);     // tooltip {top,left}, or null=centre
  const tipRef = useRef(null);
  const targetElRef = useRef(null);

  const total = steps.length;
  const step = steps[index] || null;
  const isLast = index >= total - 1;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Reset to the first step whenever the tour (re)opens.
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  // Capture the element that had focus before the tour, restore it on close.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    return () => { if (prev && typeof prev.focus === 'function') prev.focus(); };
  }, [open]);

  // Lock body scroll while the tour is active.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  // Resolve the current step's target (with a short retry window). On mobile or
  // for selectorless steps (Welcome), there's no anchor → centred.
  useLayoutEffect(() => {
    if (!open || !step) return undefined;
    targetElRef.current = null;
    if (!step.selector || isMobile) {
      setRect(null);
      return undefined;
    }
    let raf;
    let frames = 0;
    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (el) {
        targetElRef.current = el;
        try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch { /* noop */ }
        setRect(el.getBoundingClientRect());
      } else if (frames++ < 40) {
        raf = requestAnimationFrame(tryFind);
      } else {
        setRect(null); // target never showed up → degrade to centred
      }
    };
    tryFind();
    return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); };
  }, [open, index, step, isMobile]);

  // Keep the spotlight glued to the target as the layout shifts.
  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => {
      if (targetElRef.current) setRect(targetElRef.current.getBoundingClientRect());
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  // (Re)compute tooltip position once we know the target rect + tooltip size.
  useLayoutEffect(() => {
    if (!open) return;
    if (!rect) { setPos(null); return; }
    const tipEl = tipRef.current;
    const tipW = tipEl ? tipEl.offsetWidth : TIP_W;
    const tipH = tipEl ? tipEl.offsetHeight : 170;
    setPos(computePos(rect, tipW, tipH, window.innerWidth, window.innerHeight));
  }, [open, rect, index]);

  const goNext = useCallback(() => {
    setIndex((i) => (i < total - 1 ? i + 1 : i));
    if (index >= total - 1) onClose();
  }, [index, total, onClose]);
  const goBack = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // Keyboard: Esc closes, arrows/Enter step, Tab is trapped in the tooltip.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); goNext(); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goBack(); return; }
      if (e.key === 'Tab') {
        const root = tipRef.current;
        if (!root) return;
        const f = root.querySelectorAll('button:not([disabled])');
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, goNext, goBack, onClose]);

  // Move focus into the tooltip on open / step change.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      const el = tipRef.current && tipRef.current.querySelector('[data-tour-primary]');
      if (el) el.focus(); else if (tipRef.current) tipRef.current.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [open, index]);

  if (!open || !step) return null;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="aop-tour-title" aria-describedby="aop-tour-body">
      {/* Backdrop / click-catcher. When anchored, the dim comes from the
          spotlight's box-shadow and this layer is just an inert click-blocker;
          when centred, it carries the dim itself. */}
      <div
        aria-hidden="true"
        style={{ ...styles.backdrop, background: rect ? 'transparent' : 'rgba(18,34,57,0.55)' }}
      />

      {/* Spotlight cut-out around the target (anchored, non-mobile only). */}
      {rect && (
        <div
          aria-hidden="true"
          style={{
            ...styles.spotlight,
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      )}

      {/* Coachmark tooltip */}
      <div
        ref={tipRef}
        tabIndex={-1}
        style={{
          ...styles.tip,
          ...(pos
            ? { top: pos.top, left: pos.left }
            : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }),
        }}
      >
        <button style={styles.closeBtn} onClick={onClose} aria-label="Close tour">
          <X size={16} />
        </button>

        <div style={styles.counter}>Step {index + 1} of {total}</div>
        <h3 id="aop-tour-title" style={styles.title}>{step.title}</h3>
        <p id="aop-tour-body" style={styles.body}>{step.body}</p>

        <div style={styles.controls}>
          <button style={styles.skipBtn} onClick={onClose}>Skip</button>
          <div style={styles.controlsRight}>
            {index > 0 && (
              <button style={styles.backBtn} onClick={goBack}>
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <button data-tour-primary style={styles.nextBtn} onClick={goNext}>
              {isLast ? (<><Check size={14} /> Done</>) : (<>Next <ArrowRight size={14} /></>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Owner-portal tour: steps anchored to the owner nav + key surfaces. ──
// Selectors map to data-tour="…" attributes added to the Home sidebar. AU
// English, short + friendly. Welcome has no selector (intentionally centred).
const OWNER_TOUR_STEPS = [
  {
    key: 'welcome',
    title: 'Welcome to your Alloggio owner portal',
    body: "Here's a quick tour of the main areas — it only takes a moment. You can skip at any time.",
  },
  {
    key: 'home',
    selector: '[data-tour="nav-home"]',
    title: 'Home',
    body: 'Your properties at a glance, plus current bookings and anything that needs your attention.',
  },
  {
    key: 'bookings',
    selector: '[data-tour="nav-bookings"]',
    title: 'Bookings & Calendar',
    body: 'See upcoming stays and add your own owner bookings or blocks to the calendar.',
  },
  {
    key: 'performance',
    selector: '[data-tour="nav-performance"]',
    title: 'Performance',
    body: 'Track guest reviews and review scores across your properties.',
  },
  {
    key: 'financials',
    selector: '[data-tour="nav-financials"]',
    title: 'Financials',
    body: 'View your revenue and download your monthly owner statements.',
  },
  {
    key: 'documents',
    selector: '[data-tour="nav-documents"]',
    title: 'Documents',
    body: 'Find your management agreement and compliance documents, all in one place.',
  },
  {
    key: 'maintenance',
    selector: '[data-tour="nav-maintenance"]',
    title: 'Maintenance & Messages',
    body: 'Raise maintenance requests and keep track of how they’re progressing.',
  },
  // What's New was removed from this step on 2026-09-11. The tab is hidden
  // from owners (Sean, 2026-07-02) and its nav item no longer renders, so this
  // step both promised a feature nobody can reach AND anchored to
  // [data-tour="nav-whats-new"], an element that does not exist — which is why
  // it appeared as a floating box in the middle of the screen instead of a
  // tooltip pointing at anything.
  //
  // Kept as a step rather than deleted: it carries the tour's closing line, and
  // Need help? is the one control an owner most needs to know about. Now
  // anchored to the button itself.
  {
    key: 'help',
    selector: '[data-tour="help"]',
    title: 'Getting help',
    body: "Use the Need help? button up top whenever you'd like a hand. That's the tour — enjoy your portal!",
  },
];

// Mount once, app-wide. Auto-runs first time for owner-side users; replayable
// via startOwnerTour() (e.g. the Settings "Take the tour" button).
export function OwnerGuidedTour() {
  const { open, close } = useFirstRunTour();
  return <GuidedTour open={open} steps={OWNER_TOUR_STEPS} onClose={close} />;
}

const styles = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 9000 },
  spotlight: {
    position: 'fixed',
    borderRadius: 10,
    boxShadow: '0 0 0 9999px rgba(18,34,57,0.55)',
    outline: '2px solid var(--teal)',
    outlineOffset: 0,
    pointerEvents: 'none',
    transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
    zIndex: 9001,
  },
  tip: {
    position: 'fixed',
    width: TIP_W,
    maxWidth: 'calc(100vw - 32px)',
    backgroundColor: 'var(--white)',
    borderRadius: 12,
    boxShadow: 'var(--shadow-card-hover)',
    border: '1px solid var(--gray-100)',
    padding: '18px 20px 16px',
    zIndex: 9002,
    outline: 'none',
  },
  closeBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--gray-400)',
    padding: 4,
    borderRadius: 6,
    display: 'inline-flex',
  },
  counter: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: 'var(--gray-400)',
    fontWeight: 600,
    marginBottom: 6,
  },
  title: { fontSize: '16px', color: 'var(--navy)', fontWeight: 700, margin: '0 0 8px 0', letterSpacing: '-0.01em', paddingRight: 18 },
  body: { fontSize: '13.5px', color: 'var(--gray-600)', margin: '0 0 16px 0', lineHeight: 1.5 },
  controls: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  controlsRight: { display: 'flex', alignItems: 'center', gap: 8 },
  skipBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--gray-400)',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '8px 4px',
  },
  backBtn: {
    backgroundColor: 'var(--white)',
    color: 'var(--navy)',
    border: '1px solid var(--gray-200)',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  nextBtn: {
    backgroundColor: 'var(--navy)',
    color: 'var(--white)',
    border: 'none',
    borderRadius: 6,
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
};
